import { Component, ChangeDetectionStrategy, Input, ViewChild, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { TuiSelectComponent, AbstractTuiControl, TuiMultiSelectComponent } from '@taiga-ui/legacy';
import { TuiIdentityMatcher, TuiStringHandler } from '@taiga-ui/cdk';

import { InputItemType, InputSize, InputValueType, SearchCitiesMode } from '../../types';
import { SearchCitiesService } from '../../services';
import { ALL_CITIES } from '../../constants';

@Component({
  selector: 'app-search-cities-input',
  templateUrl: './search-cities-input.component.html',
  styleUrls: ['./search-cities-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchCitiesInputComponent extends AbstractTuiControl<InputValueType> {
  /**
   * Режим работы:
   * - single - выбор одной записи
   * - multiple - выбор нескольких записей
   * */
  @Input() mode: SearchCitiesMode = 'multiple';

  @Input() textfieldSize: InputSize = 'm';
  @Input() textfieldCleaner = false;
  @Input() textfieldLabelOutside = true;

  @Input() loading = false;

  @ViewChild(TuiSelectComponent)
  private readonly singleInputRef?: TuiSelectComponent<InputItemType>;

  @ViewChild(TuiMultiSelectComponent)
  private readonly multipleInputRef?: TuiMultiSelectComponent<InputItemType[]>;

  private readonly destroyed$$ = new Subject<void>();

  private readonly searchCitiesService = inject(SearchCitiesService);
  readonly topCities$ = this.searchCitiesService.topCities$;
  readonly searchCities$ = this.searchCitiesService.searchCities$;

  readonly allCities = ALL_CITIES;

  searchQuery = '';

  readonly stringify: TuiStringHandler<InputItemType> = item => item.city;
  readonly matcher: TuiIdentityMatcher<InputItemType> = (item1, item2) => item1.cityId === item2.cityId;

  get focused(): boolean {
    if (this.mode === 'single' && this.singleInputRef) {
      return this.singleInputRef.focused;
    }

    if (this.mode === 'multiple' && this.multipleInputRef) {
      return this.multipleInputRef.focused;
    }

    return false;
  }

  protected getFallbackValue(): InputValueType {
    // TODO: this.mode === undefined. Возможно this.mode не успевает подтянуться
    if (this.mode === 'single') {
      return null;
    }

    return [];
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.searchCitiesService.loadTopCities();
    this.searchCitiesService.resetSearchCities();
  }

  ngOnDestroy(): void {
    this.searchCitiesService.resetSearchCities();

    this.destroyed$$.next();
    this.destroyed$$.complete();
  }

  setValue(city: InputItemType) {
    this.value = city;
  }

  getValue() {
    if (this.mode === 'single') {
      return (this.value as InputItemType).cityId ? this.value : null;
    }

    return this.value;
  }

  onSearchChange(text: string) {
    this.searchCitiesService.searchCities(text);
  }

  setAllCities() {
    this.value = [this.allCities];
  }

  deleteAllCities() {
    const arrayWithoutAllCities = (this.value as InputItemType[])?.filter(
      item => item.cityId !== this.allCities.cityId,
    );
    this.value = arrayWithoutAllCities;
  }
}
